import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'
import VideoPreview from './VideoPreview'
import { formatDuration, formatFileSize } from '../../utils/videoMetadata'

function VideoSchedulingModal({ videoData, file, onClose }) {
  const navigate = useNavigate()
  const { selectedAccount, selectedAccountId } = useAccount()
  const [scheduledDate, setScheduledDate] = useState(null)
  const [isScheduling, setIsScheduling] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Create preview URL from file
  const videoUrl = file ? URL.createObjectURL(file) : null

  const handleSchedule = async () => {
    if (!scheduledDate || !selectedAccountId || !videoData) {
      setError('Please select a date and time')
      return
    }

    try {
      setIsScheduling(true)
      setError(null)

      // Prepare payload for /stories/schedule endpoint
      const payload = {
        media_id: videoData.media_id,
        media_type: 'video',
        instagram_account_id: selectedAccountId,
        scheduled_for: scheduledDate.toISOString(),
        blob_path: videoData.blob_path,
        metadata: videoData.metadata,
      }

      // Call schedule API
      await api.scheduleStory(payload)

      // Show success state
      setSuccess(true)

      // Clean up preview URL
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl)
      }

      // Wait a moment to show success message, then navigate
      setTimeout(() => {
        navigate('/schedule')
      }, 1500)
    } catch (err) {
      console.error('Failed to schedule video story:', err)
      setError(err.message || 'Failed to schedule video story')
    } finally {
      setIsScheduling(false)
    }
  }

  const handleClose = () => {
    // Clean up preview URL
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl)
    }
    if (onClose) {
      onClose()
    } else {
      navigate('/create')
    }
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 md:p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">Video Story Scheduled!</h3>
          <p className="text-sm text-gray-600 mb-1">
            Your video will be posted on
          </p>
          <p className="text-base font-semibold text-gray-900">
            {format(scheduledDate, 'EEEE, MMMM d, yyyy')} at {format(scheduledDate, 'HH:mm')}
          </p>
          <p className="text-xs text-gray-500 mt-4">Redirecting to schedule page...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Schedule Video Story</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Posting to: <span className="font-medium text-gray-900">@{selectedAccount?.username || selectedAccount?.instagram_username}</span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            disabled={isScheduling}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Video Preview */}
            <div>
              {videoUrl && videoData?.metadata ? (
                <VideoPreview videoUrl={videoUrl} metadata={videoData.metadata} />
              ) : (
                <div className="aspect-[9/16] bg-gray-100 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">No video preview available</p>
                </div>
              )}

              {/* Video Metadata Card */}
              {videoData?.metadata && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    Video Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {videoData.metadata.duration && (
                      <div>
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDuration(videoData.metadata.duration)}
                        </p>
                      </div>
                    )}
                    {videoData.metadata.resolution && (
                      <div>
                        <p className="text-xs text-gray-500">Resolution</p>
                        <p className="text-sm font-medium text-gray-900">
                          {videoData.metadata.resolution}
                        </p>
                      </div>
                    )}
                    {videoData.metadata.fileSize && (
                      <div>
                        <p className="text-xs text-gray-500">File Size</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatFileSize(videoData.metadata.fileSize)}
                        </p>
                      </div>
                    )}
                    {videoData.metadata.codec && (
                      <div>
                        <p className="text-xs text-gray-500">Codec</p>
                        <p className="text-sm font-medium text-gray-900">
                          {videoData.metadata.codec}
                        </p>
                      </div>
                    )}
                    {videoData.metadata.hasAudio !== undefined && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Audio</p>
                        <p className="text-sm font-medium text-gray-900">
                          {videoData.metadata.hasAudio ? '✓ Audio included' : '✗ No audio'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Scheduling Form */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Select posting date and time
                </h4>
                <p className="text-xs text-gray-500 mb-4">
                  Choose when this video should be published to your Instagram Story
                </p>

                <DatePicker
                  selected={scheduledDate}
                  onChange={(date) => setScheduledDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy HH:mm"
                  minDate={new Date()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                  placeholderText="Select date and time"
                  calendarClassName="shadow-xl"
                  inline
                />
              </div>

              {/* Selected Date Preview */}
              {scheduledDate && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">
                    Scheduled For
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {format(scheduledDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-lg font-bold text-purple-600">
                    {format(scheduledDate, 'HH:mm')}
                  </p>
                </div>
              )}

              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-xs text-blue-800">
                      Video stories are posted as-is with embedded captions. Make sure your video is ready for publishing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-6 pb-4">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
              <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isScheduling}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={isScheduling || !scheduledDate}
            className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isScheduling ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Scheduling...
              </span>
            ) : (
              'Schedule Video Story'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VideoSchedulingModal
